import type { Direction} from './direction';
import { deltaForDirection, directionForDelta } from './direction';
import { MOONWALK_TILES, MOONWALK_SLOWDOWN } from './moonwalk';

/**
 * Predicate the simulation calls to ask whether a tile may be entered. M2 binds
 * this to real map collision via {@link GameMap.walkableFn}. Passing it in
 * (rather than importing a map) keeps {@link Mobile} a pure, testable unit and
 * preserves the sim/render and sim/world seams.
 */
export type IsWalkable = (tileX: number, tileY: number) => boolean;

/**
 * Edge-based step predicate (true terrain height, PRD #536): whether a mobile
 * standing on tile `from` may step onto the adjacent tile `to`. Unlike the
 * per-cell {@link IsWalkable} it can express the **climb rule** (`|landZ(to) -
 * landZ(from)| <= CLIMB`), which depends on both endpoints. `Mobile` itself
 * stays 2D and keeps consuming an {@link IsWalkable}; the movement system
 * closes a `CanStep` over the mobile's live resting tile to produce one. On a
 * flat map every `CanStep` degenerates to the old per-cell predicate.
 */
export type CanStep = (from: TilePoint, to: TilePoint) => boolean;

export interface MobileOptions {
  x: number;
  y: number;
  facing: Direction;
  /** Milliseconds to traverse one tile at walk speed. */
  msPerTile?: number;
  /**
   * Milliseconds to traverse one tile at run speed. Defaults to half a custom
   * `msPerTile`, or to the tuned run default (200ms) when both are omitted.
   */
  msPerTileRun?: number;
  /** Milliseconds to turn in place before a step on a facing change. */
  turnDelayMs?: number;
  /**
   * Shorter turn beat used when "fast rotation" is enabled (UO's FastRotation
   * option, `TURN_DELAY_FAST`). Defaults to {@link MobileOptions.turnDelayMs}
   * when omitted, i.e. no speed-up.
   */
  turnDelayFastMs?: number;
}

export interface TilePoint {
  x: number;
  y: number;
}

/**
 * The complete, plain-data snapshot of a {@link Mobile}'s state: its
 * configuration (the gait/turn durations) plus every field of the in-progress
 * step/turn/intent. Capturing *all* of it - not just the resting tile - is what
 * lets serialization round-trip a mobile that is mid-step, so a reloaded world
 * resumes a walk exactly where it was rather than snapping to a tile boundary.
 */
export interface MobileState {
  x: number;
  y: number;
  dir: Direction;
  msPerTile: number;
  msPerTileRun: number;
  turnDelayMs: number;
  turnDelayFastMs: number;
  fastRotation: boolean;
  moving: boolean;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  elapsed: number;
  stepMs: number;
  stepRunning: boolean;
  turning: boolean;
  turnElapsed: number;
  intent: Direction | null;
  intentRunning: boolean;
  intentTurnOnly: boolean;
  /**
   * The remaining tiles of a held walk route ({@link Mobile.setPath}), in walk
   * order. Optional so snapshots that predate server-side path walking
   * deserialize as "no route".
   */
  path?: TilePoint[];
  /**
   * Tiles still to glide during an in-progress moonwalk ({@link Mobile.startMoonwalk}),
   * decremented as each backward step begins. Optional so snapshots that predate the
   * moonwalk (ADR-0145) deserialize as "not moonwalking".
   */
  moonwalkRemaining?: number;
  /**
   * Whether the in-progress step is a moonwalk glide (a slower, backward step). Optional
   * for the same back-compat reason; captured so a mid-glide mobile round-trips exactly.
   */
  stepIsMoonwalk?: boolean;
}

/**
 * Default per-tile gait durations. 250ms walk / 125ms run: brisker than
 * classic UO's authentic 400/200 - the earlier 350/200 tune still felt too
 * slow in playtesting - while staying short of the original 200/100, which
 * read as comically fast once per-step prediction made movement instant. The
 * run is exactly half the walk.
 */
const DEFAULT_MS_PER_TILE = 250;
const DEFAULT_MS_PER_TILE_RUN = 125;
/**
 * Turn-in-place beat on a facing change. Matches UO's `TURN_DELAY` (80ms): a
 * brief, speed-independent pause to pivot before stepping, distinct from the
 * per-tile step duration. The first input in a new direction turns you; the next
 * one steps.
 */
const DEFAULT_TURN_DELAY_MS = 80;

/**
 * A grid-locked actor with faithful UO movement: it always rests on a whole
 * tile, only ever steps to one of the 8 neighbours, and interpolates smoothly
 * across the gap in between. Holding an intent chains steps into continuous
 * walking; releasing it stops cleanly on the next tile boundary.
 *
 * Changing the intended facing first **turns in place** for `turnDelayMs`
 * (no positional move) and only then steps, so a quick tap spins on the spot
 * while a held key turns-then-walks. Continuous movement in a single held
 * direction never incurs a turn beat between steps. Holding the run flag makes
 * each step take the shorter run duration; the flag is sampled at step start.
 */
export class Mobile {
  private x: number;
  private y: number;
  private dir: Direction;
  private readonly msPerTile: number;
  private readonly msPerTileRun: number;
  private readonly turnDelayMs: number;
  private readonly turnDelayFastMs: number;
  private fastRotation = false;

  private moving = false;
  private fromX = 0;
  private fromY = 0;
  private toX = 0;
  private toY = 0;
  private elapsed = 0;
  /** Per-tile duration of the in-progress step (walk, run, or moonwalk glide). */
  private stepMs = DEFAULT_MS_PER_TILE;
  /**
   * Gait multiplier applied to every fresh step's duration (>1 = slower). The
   * Arena archer's Caltrops slow drives this (PRD #446 Slice 1, ADR-0237); `1`
   * is the un-slowed default and the only value town/duel mobiles ever hold.
   * Sampled at step start (`beginStep`), so it takes effect on the NEXT step,
   * never mid-glide - a clean, teardown-free way to slow a run-world enemy.
   */
  private slowFactor = 1;
  /** Whether the in-progress step is a run. */
  private stepRunning = false;
  /** Whether the in-progress step is a moonwalk glide (backward, walk clip, slower). */
  private stepIsMoonwalk = false;

  private turning = false;
  private turnElapsed = 0;

  private intent: Direction | null = null;
  private intentRunning = false;
  /**
   * The remaining tiles of a held walk route ({@link setPath}), consumed from the
   * front as each tile is reached. Mutually exclusive with {@link intent}: a route
   * clears the held direction and a fresh direction clears the route, so exactly
   * one steering mode drives {@link decide} at a time.
   */
  private path: TilePoint[] = [];
  /**
   * When set, the held {@link Mobile.intent} is a *turn-only* facing: the mobile
   * pivots to face `intent` and then idles, never stepping off its tile even
   * while the intent is held and already matches its facing. This is what lets
   * the mouse spin you in place (see ADR-0022); it can never move, so holding
   * still cannot oscillate positionally.
   */
  private intentTurnOnly = false;
  /**
   * Tiles still to glide during an in-progress moonwalk (ADR-0145): a scripted
   * backward slide that steps to the tile *behind* the current facing without ever
   * changing that facing, so the walk clip plays while the character moonwalks. It is
   * its own steering mode, mutually exclusive with {@link intent} and {@link path}:
   * starting a glide clears both, and a real steering direction / route / turn / snap
   * cancels the glide (see {@link setIntent}, {@link setPath}, {@link face},
   * {@link teleport}). `0` means "not moonwalking".
   */
  private moonwalkRemaining = 0;
  /** Per-tile duration of a moonwalk glide step: slower than a walk (a deliberate slide). */
  private readonly moonwalkMsPerTile: number;

  constructor(opts: MobileOptions) {
    this.x = opts.x;
    this.y = opts.y;
    this.dir = opts.facing;
    this.msPerTile = opts.msPerTile ?? DEFAULT_MS_PER_TILE;
    // A custom walk duration without an explicit run keeps the historical
    // half-the-walk rule; the all-defaults gait uses the tuned 250/125 pair.
    this.msPerTileRun =
      opts.msPerTileRun ??
      (opts.msPerTile !== undefined ? opts.msPerTile / 2 : DEFAULT_MS_PER_TILE_RUN);
    this.turnDelayMs = opts.turnDelayMs ?? DEFAULT_TURN_DELAY_MS;
    this.turnDelayFastMs = opts.turnDelayFastMs ?? this.turnDelayMs;
    // The glide reads as "slow": a fixed multiple of this mobile's own walk gait, so a
    // custom-gait mobile moonwalks proportionally slower too (ADR-0145).
    this.moonwalkMsPerTile = this.msPerTile * MOONWALK_SLOWDOWN;
  }

  /**
   * Capture this mobile's full state (config + in-progress step/turn/intent) as
   * plain data for serialization. The inverse of {@link Mobile.fromState}.
   *
   * `opts.path` (default `true`) controls whether the remaining walk route rides
   * along. **The wire passes `false`** (PRD #825): a route is server-side
   * steering the client never consumes - it derives movement from the replicated
   * step state (`moving`/`from`/`to`/`elapsed`/`stepMs`) and interpolates - yet
   * an Arena chaser's route is up to ~21 tiles, re-sent in full inside every
   * delta because `elapsed` ticks on every step and {@link diffEntity} is
   * per-component. Persistence still passes the default, so a saved or
   * round-tripped world resumes its routes exactly.
   */
  toState(opts: { path?: boolean } = {}): MobileState {
    return {
      x: this.x,
      y: this.y,
      dir: this.dir,
      msPerTile: this.msPerTile,
      msPerTileRun: this.msPerTileRun,
      turnDelayMs: this.turnDelayMs,
      turnDelayFastMs: this.turnDelayFastMs,
      fastRotation: this.fastRotation,
      moving: this.moving,
      fromX: this.fromX,
      fromY: this.fromY,
      toX: this.toX,
      toY: this.toY,
      elapsed: this.elapsed,
      stepMs: this.stepMs,
      stepRunning: this.stepRunning,
      turning: this.turning,
      turnElapsed: this.turnElapsed,
      intent: this.intent,
      intentRunning: this.intentRunning,
      intentTurnOnly: this.intentTurnOnly,
      path: opts.path === false ? [] : this.path.map((p) => ({ x: p.x, y: p.y })),
      moonwalkRemaining: this.moonwalkRemaining,
      stepIsMoonwalk: this.stepIsMoonwalk,
    };
  }

  /**
   * Reconstruct a mobile from a {@link toState} snapshot, restoring not just its
   * resting tile but the exact mid-step interpolation, turn beat, and intent, so
   * a deserialized world continues movement identically.
   */
  static fromState(s: MobileState): Mobile {
    const m = new Mobile({
      x: s.x,
      y: s.y,
      facing: s.dir,
      msPerTile: s.msPerTile,
      msPerTileRun: s.msPerTileRun,
      turnDelayMs: s.turnDelayMs,
      turnDelayFastMs: s.turnDelayFastMs,
    });
    m.fastRotation = s.fastRotation;
    m.moving = s.moving;
    m.fromX = s.fromX;
    m.fromY = s.fromY;
    m.toX = s.toX;
    m.toY = s.toY;
    m.elapsed = s.elapsed;
    m.stepMs = s.stepMs;
    m.stepRunning = s.stepRunning;
    m.turning = s.turning;
    m.turnElapsed = s.turnElapsed;
    m.intent = s.intent;
    m.intentRunning = s.intentRunning;
    // Older snapshots predate turn-only intents; default to a plain walk intent.
    m.intentTurnOnly = s.intentTurnOnly ?? false;
    // Older snapshots predate server-side path walking; default to no route.
    m.path = (s.path ?? []).map((p) => ({ x: p.x, y: p.y }));
    // Older snapshots predate the moonwalk glide (ADR-0145); default to "not moonwalking".
    m.moonwalkRemaining = s.moonwalkRemaining ?? 0;
    m.stepIsMoonwalk = s.stepIsMoonwalk ?? false;
    return m;
  }

  /**
   * Toggle "fast rotation" (UO's FastRotation option): when on, facing changes
   * use the shorter {@link MobileOptions.turnDelayFastMs} beat. Sampled when a
   * turn begins, so it affects subsequent turns, not one already in progress.
   */
  setFastRotation(on: boolean): void {
    this.fastRotation = on;
  }

  /** The turn-beat duration to use right now, honouring fast rotation. */
  private activeTurnDelayMs(): number {
    return this.fastRotation ? this.turnDelayFastMs : this.turnDelayMs;
  }

  get tileX(): number {
    return this.x;
  }
  get tileY(): number {
    return this.y;
  }
  get facing(): Direction {
    return this.dir;
  }
  get isMoving(): boolean {
    return this.moving;
  }
  /** True while pausing to turn in place toward a new facing. */
  get isTurning(): boolean {
    return this.turning;
  }
  /** True while the current step is a run (false when walking or idle). */
  get isRunning(): boolean {
    return this.moving && this.stepRunning;
  }
  /**
   * True while a movement intent is held, i.e. the mobile will keep stepping
   * (or step again from rest) until the intent is cleared. Read-only; the AI
   * uses it to know whether it still needs to issue a stop to park cleanly,
   * rather than spamming a stop intent every idle tick.
   */
  get hasIntent(): boolean {
    return this.intent !== null;
  }
  /**
   * True while a walk route ({@link setPath}) still holds tiles to consume.
   * Read-only; the arena horde AI (PRD #334 Slice B) uses it so an enemy that is
   * merely between steps of a live route is not re-routed on every idle poll -
   * only a consumed/abandoned route (or the repath cadence) triggers a fresh A*.
   */
  get hasPath(): boolean {
    return this.path.length > 0;
  }
  /**
   * True while a moonwalk glide is in progress (ADR-0145): either tiles remain to glide
   * or a moonwalk step is mid-slide. The client's prediction walker reads it to hold off
   * emitting per-step `Walk` requests for the scripted backward steps (the server drives
   * the glide from the single `Moonwalk` command) and to refuse re-triggering a glide
   * that is already playing.
   */
  get isMoonwalking(): boolean {
    return this.moonwalkRemaining > 0 || (this.moving && this.stepIsMoonwalk);
  }

  /**
   * Request continuous movement in a direction (or null to stop). `running`
   * is sampled when the next step begins, so toggling it mid-step affects only
   * the following step. `turnOnly` (default false) requests a turn-in-place:
   * the mobile pivots to face `dir` and then idles, never stepping - the seam
   * that lets the mouse spin you on the spot (see ADR-0022).
   */
  setIntent(dir: Direction | null, running = false, turnOnly = false): void {
    // A *real* steering direction interrupts an in-progress moonwalk glide - the player
    // took control back (ADR-0145). A `null` stop/idle does NOT cancel it: the client's
    // prediction walker calls `setIntent(null)` every frame while no key is held, and a
    // running glide must play on through those idle polls. So the glide ends only on
    // genuine steering input, never on the absence of it.
    if (dir !== null) this.moonwalkRemaining = 0;
    this.intent = dir;
    this.intentRunning = running;
    this.intentTurnOnly = turnOnly;
    // Direct steering (a held key/cursor, or an explicit stop) overrides any walk
    // route: exactly one steering mode may drive `decide` at a time.
    this.path = [];
  }

  /**
   * Request a walk along `tiles` (in order; each tile 8-adjacent to the one
   * before, the first adjacent to - or equal to - the mobile's own resting tile).
   * The mobile steps tile-to-tile at walk gait and **parks exactly on the last
   * tile**: unlike a held {@link setIntent} direction there is nothing to release,
   * so arrival cannot overshoot. A new route replaces the old; an empty `tiles`
   * stops on the next tile boundary; steering via {@link setIntent} cancels the
   * route. The tiles are copied, so the caller's array cannot mutate live state.
   *
   * The route is trusted-but-verified: {@link decide} re-syncs onto it (skipping
   * tiles already passed, joining at the nearest upcoming tile) and abandons it
   * if the mobile is not on or adjacent to any of it, so a stale route computed
   * against an old snapshot degrades to a clean stop, never a teleport or an
   * illegal step.
   */
  setPath(tiles: readonly TilePoint[]): void {
    this.path = tiles.map((p) => ({ x: p.x, y: p.y }));
    this.intent = null;
    this.intentRunning = false;
    this.intentTurnOnly = false;
    // An explicit route is direct steering; it overrides a moonwalk glide (ADR-0145).
    this.moonwalkRemaining = 0;
  }

  /**
   * Begin a moonwalk glide (ADR-0145): step to the tile **behind** the current facing
   * for up to `tiles` tiles without ever changing facing, at a slower gait, so the walk
   * clip plays while the character slides backward. The mirror of the turn-only intent
   * (which turns without stepping). It is its own steering mode - clears any held intent
   * or route - and is honoured by {@link decide} the moment the mobile is at rest; a real
   * steering direction, a route, a turn, or a hard snap cancels it. Each backward step
   * still checks {@link IsWalkable}, so the glide stops early on the last reachable tile
   * and never walks through a wall or off the map.
   */
  startMoonwalk(tiles: number = MOONWALK_TILES): void {
    this.moonwalkRemaining = Math.max(0, Math.floor(tiles));
    this.intent = null;
    this.intentRunning = false;
    this.intentTurnOnly = false;
    this.path = [];
  }

  /**
   * Set the facing instantly, with no turn beat and no positional change.
   * The server's per-step walk handler ({@link handleWalk}) applies a validated
   * turn-in-place this way: the client already played the 80ms turn beat under
   * prediction, so the authoritative side only mirrors the outcome. Cancels an
   * in-progress turn beat (the new facing wins); a step in progress is untouched.
   */
  face(dir: Direction): void {
    this.dir = dir;
    this.turning = false;
    this.turnElapsed = 0;
    // A moonwalk holds facing fixed by definition; any explicit facing change ends the
    // glide (a validated turn on the server, or a re-anchor snap - ADR-0145).
    this.moonwalkRemaining = 0;
  }

  /**
   * Move this mobile to a tile instantly: snap the resting tile and cancel any
   * in-progress step or turn beat. Facing and the held intent survive, so a
   * player walking onto a teleporter keeps walking on the other side (the next
   * `update` decides from the new tile as normal). The interpolation state is
   * fully cleared - `renderPosition()` reports the destination immediately -
   * so a teleport can never be drawn as a slide across the map.
   *
   * Two callers: the movement system's teleporter rule, and the server's
   * per-step walk handler, which applies each validated, client-predicted step
   * as a discrete snap (the client already played the interpolation).
   */
  teleport(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.moving = false;
    this.turning = false;
    this.elapsed = 0;
    this.turnElapsed = 0;
    // A hard reposition (teleporter, validated per-step snap, or re-anchor) ends any
    // moonwalk glide (ADR-0145); the interrupted step is discarded with the rest.
    this.moonwalkRemaining = 0;
    this.stepIsMoonwalk = false;
  }

  /** The tile this mobile is heading toward (its resting tile when idle). */
  targetTile(): TilePoint {
    return this.moving ? { x: this.toX, y: this.toY } : { x: this.x, y: this.y };
  }

  /**
   * Fractional tile coordinates for rendering: the exact tile when idle or
   * turning, or the interpolated point between the previous and next tile while
   * stepping.
   */
  renderPosition(): TilePoint {
    if (!this.moving) return { x: this.x, y: this.y };
    const t = this.elapsed / this.stepMs;
    return {
      x: this.fromX + (this.toX - this.fromX) * t,
      y: this.fromY + (this.toY - this.fromY) * t,
    };
  }

  /**
   * Advance the mobile by `dtMs`. From rest it decides its next action from the
   * current intent: idle, turn in place toward a new facing, or step toward the
   * facing it already holds. Leftover time carries across turn and step
   * boundaries so a single large `dt` can turn-then-step and chain steps without
   * losing a frame. `update(0, ...)` still kicks off the next action from rest.
   */
  update(dtMs: number, isWalkable: IsWalkable): void {
    if (!this.moving && !this.turning) this.decide(isWalkable);

    let budget = dtMs;
    // Consume the time budget across the current activity, chaining into the
    // next action whenever one completes with time to spare.
    for (;;) {
      if (this.turning) {
        const remaining = this.activeTurnDelayMs() - this.turnElapsed;
        if (budget < remaining) {
          this.turnElapsed += budget;
          break;
        }
        budget -= remaining;
        this.turning = false;
        this.turnElapsed = 0;
        this.decide(isWalkable);
        if (!this.moving && !this.turning) break; // turned, now idle (tap)
        if (budget <= 0) break; // re-decided action awaits next frame's time
        continue;
      }

      if (this.moving) {
        const remaining = this.stepMs - this.elapsed;
        if (budget < remaining) {
          this.elapsed += budget;
          break;
        }
        budget -= remaining;
        this.x = this.toX;
        this.y = this.toY;
        this.moving = false;
        this.elapsed = 0;
        // Only carry on if real time is left over, so a released intent (or an
        // exactly-aligned frame) parks cleanly on the boundary.
        if (budget <= 0) break;
        this.decide(isWalkable);
        if (!this.moving && !this.turning) break;
        continue;
      }

      break; // idle
    }
  }

  /**
   * From rest, pick the next action: idle when there is no intent, a turn in
   * place when the intent differs from the current facing, or a step when the
   * intent matches it.
   */
  private decide(isWalkable: IsWalkable): void {
    if (this.moonwalkRemaining > 0) {
      this.decideMoonwalkStep(isWalkable);
      return;
    }
    if (this.path.length > 0) {
      this.decidePathStep(isWalkable);
      return;
    }
    if (this.intent === null) return;
    if (this.intent !== this.dir) {
      // Turn to face the new direction; pause in place for the turn delay.
      this.dir = this.intent;
      this.turning = true;
      this.turnElapsed = 0;
      return;
    }
    // Turn-only intent: now facing the requested direction, idle - never step
    // off the tile, even while the intent is held. This is what makes a held
    // mouse-spin pivot through facings without walking (see ADR-0022).
    if (this.intentTurnOnly) return;
    // Already facing the intent: step if the target tile is walkable.
    const { dx, dy } = deltaForDirection(this.intent);
    const nx = this.x + dx;
    const ny = this.y + dy;
    if (!isWalkable(nx, ny)) return;
    this.beginStep(nx, ny, this.intentRunning);
  }

  /**
   * From rest with a route held, take the next step along it. First re-sync: the
   * route may have been computed against a snapshot a network round-trip old (the
   * mobile can have stepped on since), so consume it up to the **last** tile the
   * mobile stands on or beside - skipping the already-passed prefix, and joining
   * a route that starts one tile away (the client anchors it on the resting tile
   * it last saw) with a single adjacent step. A route with no such tile is stale
   * beyond repair: abandon it and rest, never guess.
   *
   * Then walk: consumed-to-empty means the goal tile is underfoot (arrived - the
   * exact-stop guarantee), a facing change pays the usual turn beat, and a
   * currently-blocked next tile (a closed door, in UO terms) waits in place with
   * the route held, retrying each update - so a transient block resumes by itself
   * and a permanent one is the client's cue to re-route or give up.
   *
   * An ADJACENT (Chebyshev-1) join manufactures a step the route never
   * contained, so it is only taken when that step is currently legal under the
   * predicate. This matters on heighted maps (PRD #536): a detour route bending
   * around a > CLIMB cliff can pass within one tile of the resting position at
   * a LATER index, and the old last-adjacent join stepped straight across the
   * cliff edge - refused forever by the climb-composed predicate, parking the
   * walker mid-route. Standing ON a route tile is always a sound join (the
   * next step is the route's own planned edge; if it is blocked, hold-retry -
   * the closed-door semantics above). With no legal adjacent join, fall back
   * to the raw last-adjacent one and hold-retry there (a shut door re-opens).
   */
  private decidePathStep(isWalkable: IsWalkable): void {
    let join = -1;
    let onTile = false;
    let rawJoin = -1;
    let rawOnTile = false;
    for (let i = 0; i < this.path.length; i++) {
      const p = this.path[i]!;
      const cheb = Math.max(Math.abs(p.x - this.x), Math.abs(p.y - this.y));
      if (cheb <= 1) {
        rawJoin = i;
        rawOnTile = cheb === 0;
        if (cheb === 0 || isWalkable(p.x, p.y)) {
          join = i;
          onTile = cheb === 0;
        }
      }
    }
    if (join === -1) {
      join = rawJoin;
      onTile = rawOnTile;
    }
    if (join === -1) {
      this.path = [];
      return;
    }
    this.path.splice(0, onTile ? join + 1 : join);
    const next = this.path[0];
    if (!next) return; // route fully consumed: parked exactly on its last tile
    const dir = directionForDelta(next.x - this.x, next.y - this.y);
    if (dir === null) return; // unreachable after the re-sync; defensive
    if (dir !== this.dir) {
      this.dir = dir;
      this.turning = true;
      this.turnElapsed = 0;
      return;
    }
    if (!isWalkable(next.x, next.y)) return; // blocked: hold the route, retry next update
    this.beginStep(next.x, next.y, false); // a routed walk is always walk gait
  }

  /**
   * From rest with a moonwalk glide held, take the next backward step (ADR-0145): the
   * tile **behind** the current facing (facing itself never changes, so the walk clip
   * plays over an unchanged pose). Walkability is honoured exactly like a forward step -
   * a blocked tile behind (wall, water, closed door, map edge) ends the glide on the last
   * reachable tile rather than stepping through it. Each committed step spends one of the
   * remaining tiles and runs at the slower moonwalk gait.
   */
  private decideMoonwalkStep(isWalkable: IsWalkable): void {
    const { dx, dy } = deltaForDirection(this.dir);
    const nx = this.x - dx;
    const ny = this.y - dy;
    if (!isWalkable(nx, ny)) {
      this.moonwalkRemaining = 0; // blocked: stop early on the last reachable tile
      return;
    }
    this.moonwalkRemaining -= 1;
    this.beginStep(nx, ny, false, true);
  }

  /**
   * Start a step from the resting tile to the (already validated) neighbour `(nx, ny)`.
   * `moonwalk` marks a backward glide step (ADR-0145): it runs at the slower moonwalk
   * gait and is never a run, but is otherwise an ordinary step (the walk clip plays).
   */
  private beginStep(nx: number, ny: number, running: boolean, moonwalk = false): void {
    this.fromX = this.x;
    this.fromY = this.y;
    this.toX = nx;
    this.toY = ny;
    this.stepRunning = running;
    this.stepIsMoonwalk = moonwalk;
    const base = moonwalk ? this.moonwalkMsPerTile : running ? this.msPerTileRun : this.msPerTile;
    this.stepMs = base * this.slowFactor;
    this.elapsed = 0;
    this.moving = true;
  }

  /**
   * Set the gait slow multiplier (>1 slows the next step, `1` clears it) - the
   * Arena Caltrops slow (ADR-0237). Sampled at each {@link beginStep}, so it
   * changes the pace from the following step onward, never the in-flight one.
   */
  setSlowFactor(factor: number): void {
    this.slowFactor = factor > 0 ? factor : 1;
  }

  /** The current gait slow multiplier (1 = un-slowed). */
  get gaitSlowFactor(): number {
    return this.slowFactor;
  }
}
